import { type ReactNode, useCallback } from 'react'

import type { Localizable } from '@salsita-ai/platform-common/utils'
import type { DomainEntityId } from '@salsita-ai/platform-project-sdk/domain'
import {
  DomainControlsAnalytics,
  useDistanceDimension,
  useResolveLocalizable,
} from '@salsita-ai/platform-project-sdk/react'
import { NumericalInput } from '@salsita-ai/platform-project-sdk/ui'

/**
 * Props for the DistanceDimensionInput Phoenix element.
 * Renders a numeric input that reads/writes a distanceDimension component instance.
 */
export interface DistanceDimensionInputProps {
  /**
   * DomainEntityId to be updated.
   */
  entityId: DomainEntityId<string>
  /**
   * The name of the instance to be used for the dimension update.
   */
  instanceName: string
  /**
   * An array of fixable violations. They will be ignored in the violation hint.
   */
  fixableViolations?: string[]
  helperText?: Localizable
  /** Optional start adornment to render inside the input field. */
  startAdornment?: ReactNode
  /** Optional localized label for the input field. */
  inputLabel?: Localizable
}

export const CustomDistanceDimensionInput = (props: DistanceDimensionInputProps) => {
  const { entityId, instanceName, helperText, startAdornment, inputLabel, fixableViolations } = props

  const resolveLocalizable = useResolveLocalizable()
  const { min, max, value, unitLabel, hints, hasErrors, update, resolve } = useDistanceDimension(
    entityId,
    instanceName,
    fixableViolations,
    DomainControlsAnalytics.DistanceDimensionInputChanged,
    false
  )

  const updateAndResolve = useCallback(() => {
    update(value)
    resolve()
  }, [update, resolve, value])

  return (
    <NumericalInput
      value={value}
      min={min ?? 0}
      max={max ?? 700}
      onChange={update}
      onChanged={updateAndResolve}
      unit={unitLabel}
      hasError={hasErrors}
      hintMessage={hints}
      helperText={resolveLocalizable(helperText)}
      inputFieldProps={{
        label: resolveLocalizable(inputLabel),
        startAdornment,
        invalidNumberErrorMessage: 'Please provide a valid number!',
      }}
    />
  )
}
